'use client';

/**
 * PropertyEnquiryModal - Property Information Request Form
 *
 * Opens when the user clicks "Request Information" on the property contact card.
 * Submits to /api/contact with the property name pre-filled.
 */

import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { analyticsEvents, setEnhancedConversionData } from '@/components/analytics/GoogleAnalytics';
import { collectAttribution } from '@/lib/attribution';
import { TurnstileWidget } from '@/components/ui/TurnstileWidget';
import {
  X,
  User,
  Mail,
  Phone,
  MessageSquare,
  Loader2,
  Check,
  AlertCircle,
  Home,
  Download,
} from 'lucide-react';

export interface PropertyEnquiryModalProps {
  propertyId: string;
  propertyName: string;
  /** Property price — passed as conversion value to analytics */
  propertyPrice?: number | undefined;
  onClose: () => void;
  /** Additional class name applied to the modal container for theme overrides */
  className?: string;
  /** When set, the success screen offers a brochure download */
  brochureDocumentId?: string | undefined;
  /** Organisation ID for the brochure download proxy */
  organisationId?: string | undefined;
  /** Lead intent passed through to /api/contact (e.g. buyer) */
  intent?: string | undefined;
  messages?: {
    title?: string;
    subtitle?: string;
    firstName?: string;
    lastName?: string;
    emailAddress?: string;
    phoneNumber?: string;
    message?: string;
    messagePlaceholder?: string;
    submit?: string;
    submitting?: string;
    success?: string;
    successMessage?: string;
    error?: string;
    errorMessage?: string;
    close?: string;
    required?: string;
    invalidEmail?: string;
  };
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
}

type ModalStep = 'form' | 'submitting' | 'success' | 'error';

const defaultMessages = {
  title: 'Request Information',
  subtitle: 'Fill in your details and we\'ll get back to you shortly.',
  firstName: 'First Name',
  lastName: 'Last Name',
  emailAddress: 'Email Address',
  phoneNumber: 'Phone Number (Optional)',
  message: 'Your Message',
  messagePlaceholder: 'I\'d like more information about this property...',
  submit: 'Send Request',
  submitting: 'Sending...',
  success: 'Request Sent!',
  successMessage: 'We\'ve received your enquiry about {propertyName}. Our team will be in touch shortly.',
  error: 'Something went wrong',
  errorMessage: 'Please try again or contact us directly.',
  close: 'Done',
  required: 'This field is required',
  invalidEmail: 'Please enter a valid email',
};

export function PropertyEnquiryModal({
  propertyId,
  propertyName,
  propertyPrice,
  onClose,
  className = '',
  brochureDocumentId,
  organisationId,
  intent,
  messages: customMessages = {},
}: PropertyEnquiryModalProps) {
  const messages = { ...defaultMessages, ...customMessages };

  const [step, setStep] = useState<ModalStep>('form');
  const [isDownloading, setIsDownloading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: brochureDocumentId
      ? `I'd like to download the brochure for ${propertyName}.`
      : `I'm interested in ${propertyName} and would like more information.`,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  useEffect(() => {
    analyticsEvents.beginEnquiry(propertyId, propertyName, propertyPrice);
  }, [propertyId, propertyName, propertyPrice]);

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.firstName.trim()) newErrors.firstName = messages.required;
    if (!formData.lastName.trim()) newErrors.lastName = messages.required;
    if (!formData.email.trim()) {
      newErrors.email = messages.required;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = messages.invalidEmail;
    }
    if (!formData.message.trim()) newErrors.message = messages.required;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, messages]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;

    setStep('submitting');

    try {
      const attribution = collectAttribution();
      const locale = typeof window !== 'undefined'
        ? window.location.pathname.split('/')[1] || 'en'
        : 'en';

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone || undefined,
          message: formData.message,
          propertyType: 'other',
          propertyId,
          propertyName,
          locale,
          ...(intent && { intent }),
          metadata: {
            ...attribution,
            ...(intent && { intent }),
          },
          turnstileToken: turnstileToken || undefined,
        }),
      });

      if (!response.ok) throw new Error('Failed to send');

      setStep('success');

      setEnhancedConversionData({
        email: formData.email,
        phone: formData.phone,
        firstName: formData.firstName,
        lastName: formData.lastName,
      });
      analyticsEvents.contactProperty(propertyId, propertyName, 'request_info');
      analyticsEvents.formSubmit('property_enquiry', window.location.pathname);
      analyticsEvents.generateLead('property_enquiry', propertyPrice, {
        propertyId,
        propertyName,
        email: formData.email,
        phone: formData.phone,
        firstName: formData.firstName,
        lastName: formData.lastName,
      });
    } catch {
      setStep('error');
    }
  }, [validateForm, formData, propertyId, propertyName, propertyPrice, intent, turnstileToken]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name as keyof FormData]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    },
    [errors],
  );

  const handleBrochureDownload = useCallback(async () => {
    if (!brochureDocumentId || !organisationId) return;
    setIsDownloading(true);
    try {
      const res = await fetch(
        `/api/documents/download?organisationId=${encodeURIComponent(organisationId)}&documentId=${encodeURIComponent(brochureDocumentId)}`
      );
      if (!res.ok) throw new Error('Download failed');
      const data = await res.json();
      if (data.downloadUrl) {
        window.open(data.downloadUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('[Brochure] Download failed:', err);
    } finally {
      setIsDownloading(false);
    }
  }, [brochureDocumentId, organisationId]);

  const cssVarDefaults = {
    '--enquiry-primary': '#243462',
    '--enquiry-primary-dark': '#1a2647',
    '--enquiry-accent': '#BBA339',
    '--enquiry-accent-light': '#E4CE4A',
  } as Record<string, string>;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${className}`}
      style={cssVarDefaults}
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="enquiry-modal-panel relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        {/* Header */}
        <div
          className="enquiry-modal-header p-6 pb-16"
          style={{ background: `linear-gradient(to bottom right, var(--enquiry-primary), var(--enquiry-primary-dark))` }}
        >
          <div className="flex items-center gap-4">
            <div
              className="enquiry-modal-icon relative w-14 h-14 rounded-full overflow-hidden shadow-xl flex items-center justify-center"
              style={{
                boxShadow: `0 0 0 2px color-mix(in srgb, var(--enquiry-accent) 30%, transparent)`,
                background: `linear-gradient(to bottom right, var(--enquiry-accent), var(--enquiry-accent-light))`,
              }}
            >
              <Home className="w-7 h-7 text-white" />
            </div>
            <div>
              <h3 className="enquiry-modal-title text-xl font-bold text-white">
                {messages.title}
              </h3>
              <p className="text-sm text-white/80 truncate max-w-[250px]">
                {propertyName}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="enquiry-modal-content relative -mt-8 mx-4 mb-4 bg-white rounded-2xl shadow-lg border border-gray-100">
          <AnimatePresence mode="wait">
            {/* Form */}
            {step === 'form' && (
              <motion.div
                key="form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-5"
              >
                <p className="text-sm text-gray-500 mb-4">
                  {messages.subtitle}
                </p>

                <form className="space-y-3">
                  {/* Name row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          placeholder={messages.firstName}
                          className={`enquiry-modal-input w-full pl-10 pr-4 py-2.5 text-sm border rounded-xl outline-none transition-all ${
                            errors.firstName ? 'border-red-500' : 'border-gray-200'
                          }`}
                          style={{ '--tw-ring-color': `color-mix(in srgb, var(--enquiry-primary) 20%, transparent)` } as React.CSSProperties}
                          onFocus={(e) => { e.target.style.borderColor = `var(--enquiry-primary)`; e.target.style.boxShadow = `0 0 0 2px color-mix(in srgb, var(--enquiry-primary) 20%, transparent)`; }}
                          onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                        />
                      </div>
                      {errors.firstName && (
                        <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>
                      )}
                    </div>
                    <div>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          placeholder={messages.lastName}
                          className={`enquiry-modal-input w-full pl-10 pr-4 py-2.5 text-sm border rounded-xl outline-none transition-all ${
                            errors.lastName ? 'border-red-500' : 'border-gray-200'
                          }`}
                          onFocus={(e) => { e.target.style.borderColor = `var(--enquiry-primary)`; e.target.style.boxShadow = `0 0 0 2px color-mix(in srgb, var(--enquiry-primary) 20%, transparent)`; }}
                          onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                        />
                      </div>
                      {errors.lastName && (
                        <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={messages.emailAddress}
                        className={`enquiry-modal-input w-full pl-10 pr-4 py-2.5 text-sm border rounded-xl outline-none transition-all ${
                          errors.email ? 'border-red-500' : 'border-gray-200'
                        }`}
                        onFocus={(e) => { e.target.style.borderColor = `var(--enquiry-primary)`; e.target.style.boxShadow = `0 0 0 2px color-mix(in srgb, var(--enquiry-primary) 20%, transparent)`; }}
                        onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                      />
                    </div>
                    {errors.email && (
                      <p className="text-xs text-red-500 mt-1">{errors.email}</p>
                    )}
                  </div>

                  {/* Phone */}
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={messages.phoneNumber}
                      className="enquiry-modal-input w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none transition-all"
                      onFocus={(e) => { e.target.style.borderColor = `var(--enquiry-primary)`; e.target.style.boxShadow = `0 0 0 2px color-mix(in srgb, var(--enquiry-primary) 20%, transparent)`; }}
                      onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder={messages.messagePlaceholder}
                        rows={3}
                        className={`enquiry-modal-input w-full pl-10 pr-4 py-2.5 text-sm border rounded-xl outline-none transition-all resize-none ${
                          errors.message ? 'border-red-500' : 'border-gray-200'
                        }`}
                        onFocus={(e) => { e.target.style.borderColor = `var(--enquiry-primary)`; e.target.style.boxShadow = `0 0 0 2px color-mix(in srgb, var(--enquiry-primary) 20%, transparent)`; }}
                        onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                      />
                    </div>
                    {errors.message && (
                      <p className="text-xs text-red-500 mt-1">{errors.message}</p>
                    )}
                  </div>

                  {/* Bot Protection */}
                  <TurnstileWidget
                    onVerify={(token: string) => setTurnstileToken(token)}
                    onExpire={() => setTurnstileToken(null)}
                    className="flex justify-center"
                  />

                  {/* Submit */}
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="enquiry-modal-submit w-full flex items-center justify-center gap-2 px-6 py-3 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                    style={{ background: `linear-gradient(to right, var(--enquiry-primary), var(--enquiry-primary-dark))` }}
                  >
                    <Mail className="w-5 h-5" />
                    {messages.submit}
                  </button>
                </form>
              </motion.div>
            )}

            {/* Submitting */}
            {step === 'submitting' && (
              <motion.div
                key="submitting"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="p-8 flex flex-col items-center justify-center min-h-[250px]"
              >
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                  style={{ backgroundColor: `color-mix(in srgb, var(--enquiry-primary) 10%, transparent)` }}
                >
                  <Loader2 className="w-8 h-8 animate-spin" style={{ color: `var(--enquiry-primary)` }} />
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {messages.submitting}
                </p>
              </motion.div>
            )}

            {/* Success */}
            {step === 'success' && (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="p-6 text-center"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', damping: 15, stiffness: 300, delay: 0.1 }}
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{
                    background: `linear-gradient(to bottom right, color-mix(in srgb, var(--enquiry-accent) 20%, transparent), color-mix(in srgb, var(--enquiry-accent-light) 20%, transparent))`,
                    boxShadow: `0 10px 15px -3px color-mix(in srgb, var(--enquiry-accent) 10%, transparent)`,
                  }}
                >
                  <Check className="w-8 h-8" style={{ color: `var(--enquiry-accent)` }} strokeWidth={3} />
                </motion.div>

                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  {messages.success}
                </h4>
                <p className="text-sm text-gray-600 mb-6">
                  {messages.successMessage.replace('{propertyName}', propertyName)}
                </p>

                <div className="space-y-3">
                  {brochureDocumentId && organisationId && (
                    <button
                      onClick={handleBrochureDownload}
                      disabled={isDownloading}
                      className="enquiry-modal-submit w-full flex items-center justify-center gap-2 px-6 py-3 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-60"
                      style={{ background: `linear-gradient(to right, var(--enquiry-accent), var(--enquiry-accent-light))` }}
                    >
                      {isDownloading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Download className="w-5 h-5" />
                      )}
                      {isDownloading ? 'Preparing...' : 'Download Brochure'}
                    </button>
                  )}

                  <button
                    onClick={onClose}
                    className="enquiry-modal-submit w-full px-6 py-3 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                    style={{ background: `linear-gradient(to right, var(--enquiry-primary), var(--enquiry-primary-dark))` }}
                  >
                    {messages.close}
                  </button>
                </div>
              </motion.div>
            )}

            {/* Error */}
            {step === 'error' && (
              <motion.div
                key="error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="p-8 flex flex-col items-center justify-center min-h-[250px]"
              >
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  {messages.error}
                </h4>
                <p className="text-sm text-gray-600 text-center mb-6">
                  {messages.errorMessage}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setStep('form')}
                    className="enquiry-modal-submit px-6 py-2.5 text-white rounded-xl font-medium transition-colors"
                    style={{ backgroundColor: `var(--enquiry-primary)` }}
                  >
                    Try Again
                  </button>
                  <button
                    onClick={onClose}
                    className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                  >
                    {messages.close}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
}
